import { StyledComponentProps } from "@mui/styles";
import { FeedClassKey } from "assets/jss/common-styles";
import IconButtonCheckComplete from "components/Buttons/IconButtonCheckComplete";
import IconButtonCollapseContent from "components/Buttons/IconButtonCollapseContent";
import IconButtonDelete from "components/Buttons/IconButtonDelete";
import IconButtonEdit from "components/Buttons/IconButtonEdit";
import IconButtonHide from "components/Buttons/IconButtonHide";
import IconButtonUndelete from "components/Buttons/IconButtonUndelete";
import IconButtonViewLater from "components/Buttons/IconButtonViewLater";
import IconCheckComplete from "components/Icons/IconCheckComplete";
import MuiFakeIconButton from "components/MuiExtras/Button/MuiFakeIconButton";
import { withSnackbar, WithSnackbar } from "components/SnackbarProvider/Snackbar";
import { CourseItemStatus, FeedType, PublicationType } from "models/ApiEnums";
import { FeedItemDto } from "models/ApiModels";
import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { CourseActions } from "views/Publication/Components/CourseModels";
import { CardBodyData } from "views/Timeline/Components/CardLib/CardBodyData";

/////////////////////////////////////////////////////////////////////////////////////

interface IState {
    dialog: any;
}

interface IProps {
    data: FeedItemDto;
    feedType: FeedType;
    mouseOver: boolean;
    textExpanded: boolean;

    onHidePublication?: () => void;
    onRemovePublication?: () => void;
    onEditPublication?: () => void;
    onTextExpandedChange?: (expanded: boolean) => void;
    onViewLater?: (publicationId: string, value: boolean) => void;
    onCustomAction?: (action: any) => void;
}

type Props = IProps & StyledComponentProps<FeedClassKey> & WithSnackbar & WithTranslation;

type IconItem = {
    node: React.ReactNode;
    visible: boolean;
};

/////////////////////////////////////////////////////////////////////////////////////

class IconsLine extends React.PureComponent<Props, IState> {

    constructor(props: Props) {
        super(props);
        this.state = {
            dialog: null,
        };
    }

    ///
    private getIcons() {

        const { feedType, textExpanded, mouseOver, data, data: { publicationId, publicationType, interaction, permissions, publication: { courseItem } } } = this.props;

        const icons: IconItem[] = [];
        let index = 0;

        // Se não tem permissão, não mostra ícones
        if (!permissions.hasAccess)
            return icons;

        // Tarefas não mostram ícones
        if (feedType == FeedType.TaskDenouncement || feedType == FeedType.TaskForYourAction || feedType == FeedType.TaskForYourApproval)
            return icons;

        /*
         * ORDENAÇÃO: QUANTO menor o 'index' maior a prioridade de exibição.
         * 
         */

        // WORKITEM 585: não mostrar "Ver menos" nos cards.
        // Então o ícone de "contrair" deve ter prioridade de exibição.
        if (this.props.onTextExpandedChange && textExpanded === true) {
            icons.push({
                node: <IconButtonCollapseContent key={index++} visible={mouseOver} onClick={() => { this.props.onTextExpandedChange(!textExpanded); }} size="small" />,
                visible: mouseOver
            });
        }

        /////////////////////////////////////////////////////

        if (feedType == FeedType.CourseViewer) {
            if (!this.props.onTextExpandedChange || textExpanded !== false || interaction.courseItem.completedOn != null) {
                if (interaction.courseItem.completedOn == null) {

                    if (interaction.courseEnroll.completedOn == null && !interaction.courseEnroll.expired) {

                        //Carregar o CardBody para centralizar verificações de Link e Arquivos anexados a postagem 
                        const cardBody = new CardBodyData(data, false, false);

                        // Ainda pode marcar como completado.
                        if (interaction.courseItem.containVideo || interaction.courseItem.containEmbedVideo || cardBody.hasLink() || cardBody.hasFile())
                            // Automático
                            icons.push({
                                node: <MuiFakeIconButton size="small">
                                    <IconCheckComplete
                                        key={index++}
                                        title={this.props.t("Common.Feed.Literals.CourseNotCompleteText")}
                                        color="muted"
                                        fixedWidth
                                    />
                                </MuiFakeIconButton>,
                                visible: true
                            });
                        else
                            // Manual
                            icons.push({
                                node: <IconButtonCheckComplete
                                    key={index++}
                                    title={this.props.t("Common.Feed.Literals.CourseMarkCompleteText")}
                                    size="small"
                                    onClick={
                                        () => {
                                            this.props.onCustomAction({ execution: { actionName: "complete" } as CourseActions });
                                        }
                                    }
                                />,
                                visible: true
                            });
                    }
                    else {
                        // Não pode mais completar marcar como completado.
                        icons.push({
                            node: <MuiFakeIconButton size="small">
                                <IconCheckComplete
                                    key={index++}
                                    color="mutedDark"
                                    fixedWidth />
                            </MuiFakeIconButton>,
                            visible: true
                        });
                    }
                }
                else {
                    // Já completado.
                    icons.push({
                        node: <MuiFakeIconButton size="small">
                            <IconCheckComplete key={index++}
                                title={this.props.t("Common.Feed.Literals.CourseCompleteText")}
                                color="success"
                                fixedWidth
                            />
                        </MuiFakeIconButton>,
                        visible: true
                    });
                }
            }
        }

        /////////////////////////////////////////////////////

        if (feedType != FeedType.CourseItems) {

            if (this.props.onHidePublication) {
                icons.push({
                    node: <IconButtonHide key={index++} visible={mouseOver} onClick={() => { this.props.onHidePublication(); }} size="small" />,
                    visible: mouseOver
                });
            }

            // Retirado: WorkItem 251
            //if (icons.length < 2 && this.props.onTextExpandedChange && textExpanded === false)
            //    icons.push(<IconButtonExpandContent key="textExpand" visible={mouseOver} onClick={() => { this.props.onTextExpandedChange(!textExpanded) }} size="small" />);

            if (this.props.onViewLater && publicationType != PublicationType.Poll) {
                icons.push({
                    node:
                        <IconButtonViewLater
                            size="small"
                            key={index++}
                            visible={mouseOver || interaction.inViewLaterList}
                            pressed={interaction.inViewLaterList}
                            color={interaction.inViewLaterList ? "success" : null}
                            onClick={() => this.props.onViewLater(publicationId, !interaction.inViewLaterList)}
                        />,
                    visible: mouseOver || interaction.inViewLaterList
                });
            }

        }

        /////////////////////////////////////////////////////

        if (feedType == FeedType.CourseItems && permissions.canDelete && this.props.onRemovePublication) {
            if (courseItem.editStatus == CourseItemStatus.Active || courseItem.editStatus == CourseItemStatus.MarkToInsert) {
                icons.push({
                    node: <IconButtonDelete key={index++} visible={mouseOver} color="error" onClick={() => this.props.onRemovePublication()} size="small" />,
                    visible: mouseOver
                });
            }
            else {
                icons.push({
                    node: <IconButtonUndelete key={index++} visible={mouseOver} color="success" onClick={() => this.props.onRemovePublication()} size="small" />,
                    visible: mouseOver
                });
            }
        }

        /////////////////////////////////////////////////////

        if (feedType == FeedType.List && permissions.canDelete && this.props.onRemovePublication) {
            icons.push({
                node: <IconButtonDelete key={index++} visible={mouseOver} color="error" onClick={() => this.props.onRemovePublication()} size="small" />,
                visible: mouseOver
            });
        }

        /////////////////////////////////////////////////////

        if (permissions.canEdit && this.props.onEditPublication)
            icons.push({
                node: <IconButtonEdit key={index++} visible={mouseOver} onClick={() => this.props.onEditPublication()} size="small" />,
                visible: mouseOver
            });

        /////////////////////////////////////////////////////

        return icons;
    }

    ///
    render() {
        let icons = this.getIcons();
        const allVisibile = icons.filter(p => p.visible);
        if (allVisibile.length > 0) icons = allVisibile;

        return (
            <div className={this.props.classes.iconsContainer}>
                {icons.reverse().slice(Math.max(icons.length - 2, 0)).map(m => m.node)}
            </div>
        );
    }
}

export default withSnackbar(withTranslation()(IconsLine));
